// Production-variant analog of `button-element-mixin.js`. Mirrors the same
// `optionBundles`, branding type, and `optionsOrder` so the resolver returns
// identical values for the same persisted options. Keeps the visible
// computeds from `SharedButtonComputedStylesMixin` (already render-focused —
// no iRichText, no inline-edit watchers, no Pinia writable state) and adds
// the small text-style projection iButton's label needs.
//
// Skips: the inline-edit click handler, the `mapWritableState` over
// `currentlyInspectedElementIdentifiers`, the `textOptionValue`-to-
// `initialValue` watcher, and the `setDefaultBrandingValue` stash. Selection
// re-routes mounting back to the Builder variant so the active button still
// gets the rich-text editor when the user clicks into it.

import { DynamicElementMixin } from "%/dynamic-component-editor/mixins/dynamic-element-mixin";
import {
  SharedButtonComputedStylesMixin,
} from "%/dynamic-component-editor/standard-elements/button-element/shared-button-computed-styles-mixin";
import ButtonBranding from "%/dynamic-component-editor/classes/branding/button-branding";
import ButtonOptions from "%/dynamic-component-editor/bundles/button-options";
import ButtonBorderOptions from "%/dynamic-component-editor/bundles/button-border-options";
import PositioningOptions from "%/dynamic-component-editor/bundles/positioning-options";
import LayoutWidthHeightOptions from "%/dynamic-component-editor/bundles/layout-width-height-options";
import AppearanceOptions from "%/dynamic-component-editor/bundles/appearance-options";
import EffectsOptions from "%/dynamic-component-editor/bundles/effects-options";
import AnimationEffectsOptions from "%/dynamic-component-editor/bundles/animation-effects-options";
import SpacingOptions from "%/dynamic-component-editor/bundles/spacing-options";
import BasicTextOptions from "%/dynamic-component-editor/bundles/basic-text-options";
import TextOptions from "%/dynamic-component-editor/bundles/text-options";
import BackgroundOptions from "%/dynamic-component-editor/bundles/background-options";
import BaseOption from "%/dynamic-component-editor/classes/base-option";
import FontFamily from "%/dynamic-component-editor/options/font-family";
import FontSize from "%/dynamic-component-editor/options/font-size";
import FontColor from "%/dynamic-component-editor/options/font-color";
import normalizeFontSizeStyleValue from "%/dynamic-component-editor/helpers/font-size-style-value";
import FontWeight from "%/dynamic-component-editor/options/font-weight";
import FontCase from "%/dynamic-component-editor/options/font-case";
import FontStyle from "%/dynamic-component-editor/options/font-style";
import LineHeightMultiplier from "%/dynamic-component-editor/options/line-height-multiplier";
import TextWrap from "%/dynamic-component-editor/options/text-wrap";
import TextAlign from "%/dynamic-component-editor/options/text-align";
import TextDecoration from "%/dynamic-component-editor/options/text-decoration";
import TextOverflow from "%/dynamic-component-editor/options/text-overflow";
import MaxVerticalLines from "%/dynamic-component-editor/options/max-vertical-lines";
import TextOption from "%/dynamic-component-editor/options/text-option";
import Label from "%/dynamic-component-editor/options/label";
import ClickAction from "%/dynamic-component-editor/options/click-action";

export const ButtonElementRenderMixin = {
  brandingType: ButtonBranding,
  mixins: [DynamicElementMixin, SharedButtonComputedStylesMixin],
  optionsOrder: [
    "button",
    "click-action",
  ],
  optionBundles: [
    AnimationEffectsOptions,
    new EffectsOptions({}, {
      "text-shadow": true,
    }),
    PositioningOptions,
    new LayoutWidthHeightOptions({
      "default.layout-width": "hug",
      "default.layout-height": "hug",
    }),
    new AppearanceOptions({
      "default.border-radius": "large",
    }, {
      "border-radius": false,
    }),
    new ButtonOptions({ "default.icon": "" }),
    ButtonBorderOptions,
    new SpacingOptions({
      "default.padding": {
        "top": null,
        "left": null,
        "bottom": null,
        "right": null,
      },
    }),
    new BasicTextOptions({
      "default.font-color": {
        value: "light",
        source: "theme.font",
      },
    }),
    new TextOptions({
      "default.text-wrap": "nowrap",
    }),
    new BackgroundOptions({
      "default.background-color": {
        value: "unset",
        source: "theme.background",
      },
    }, {
      "background-image": true,
    }),
  ],
  options: [
    ClickAction,
  ],
  computed: {
    textOnlyAttributes() {
      return {
        maxVerticalLines: this.getOptionValue(MaxVerticalLines),
        overflow: this.getOptionValue(TextOverflow),
        wrap: this.getOptionValue(TextWrap),
        textAlign: this.getOptionValue(TextAlign),
        textCase: this.getOptionValue(FontCase),
        lineHeightMultiplier: this.getOptionValue(LineHeightMultiplier),
        textStyleOverrides: {
          standardFontFamily: this.getOptionValue(FontFamily) || "standard",
          standardFontSize: normalizeFontSizeStyleValue(this.getOptionValue(FontSize), { element: "ButtonElement", option: "font-size" }),
          standardFontColor: this.getOptionValue(FontColor) || "standard",
          standardFontWeight: this.getOptionValue(FontWeight) || "standard",
          standardHoverFontFamily: this.getOptionValue(FontFamily, BaseOption.$states.HOVER) || "standard",
          standardHoverFontSize: normalizeFontSizeStyleValue(this.getOptionValue(FontSize, BaseOption.$states.HOVER), { element: "ButtonElement", option: "hover.font-size" }),
          standardHoverFontColor: this.getOptionValue(FontColor, BaseOption.$states.HOVER) || "standard",
          standardHoverFontWeight: this.getOptionValue(FontWeight, BaseOption.$states.HOVER) || "standard",
          textDecoration: this.getOptionValue(TextDecoration),
          textDecorationHover: this.getOptionValue(TextDecoration, BaseOption.$states.HOVER),
          textStyle: this.getOptionValue(FontStyle),
          textStyleHover: this.getOptionValue(FontStyle, BaseOption.$states.HOVER),
        },
      };
    },
    fontColor() {
      return this.getOptionValue(FontColor);
    },
    fontSizeNumberValue() {
      const value = this.getOptionValue(FontSize);
      if (typeof value !== "string") {
        return 0;
      }
      return parseInt(value.replace("px", ""));
    },
    textValueOptionClass() {
      return TextOption;
    },
    textValueKey() {
      return undefined;
    },
    textOptionValue() {
      let textValue = this.getOptionValue(this.textValueOptionClass) || "";
      if (this.textValueKey && textValue) {
        if (this.textValueKey === "label") {
          if (!textValue.hasUpdated) {
            textValue = this.getOptionValue(Label);
          } else {
            textValue = textValue[this.textValueKey];
          }
        } else {
          textValue = textValue[this.textValueKey];
        }
      }
      return textValue;
    },
    renderedText() {
      const value = this.textOptionValue;
      if (typeof value === "string") {
        return value;
      }
      if (value && typeof value === "object" && typeof value.body_text === "string") {
        return value.body_text;
      }
      return "";
    },
    hasTextContent() {
      const value = this.textOptionValue;
      if (!value) {
        return false;
      }
      if (typeof value === "string") {
        return value.length > 0;
      }
      const body = value.body_text;
      return typeof body === "string" && body.length > 0 && body !== "\n";
    },
    editable() {
      return false;
    },
  },
};
